// src/Dashboard/pages/teacher/Attendance/AttendanceDashboard.jsx

import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";
import { useUser } from "../../../../pages/Home/UserContext";

import { useAttendanceDashboard } from "./hooks/useAttendanceDashboard";

import AttendanceDashboardHeader from "./components/AttendanceDashboardHeader";
import AttendanceGroupPickerPanel from "./components/AttendanceGroupPickerPanel";
import AttendanceFiltersCard from "./components/AttendanceFiltersCard";
import AttendanceKpiCards from "./components/AttendanceKpiCards";
import AttendanceAdherentsGrid from "./components/AttendanceAdherentsGrid";
import AttendanceEmptyState from "./components/AttendanceEmptyState";

function getStoredUserInfo() {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getRoleId(user, storedUser) {
  return Number(
    user?.role_id ||
      user?.roleId ||
      storedUser?.role_id ||
      storedUser?.roleId ||
      0
  );
}

function UnauthorizedPanel() {
  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] px-3 sm:px-4 lg:px-6">
        <div className="overflow-hidden rounded-[28px] border border-rose-200 bg-white shadow-sm dark:border-rose-500/20 dark:bg-slate-900">
          <div className="h-1.5 bg-gradient-to-r from-rose-500 to-orange-500" />

          <div className="p-5 sm:p-6">
            <p className="text-[11px] uppercase tracking-[0.22em] text-rose-500">
              Accès refusé
            </p>

            <h1 className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              Présence des adhérents
            </h1>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Cette page est réservée aux administrateurs et enseignants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AttendanceDashboard() {
  useScrollToTop();

  const { user } = useUser();
  const storedUser = useMemo(() => getStoredUserInfo(), []);
  const roleId = getRoleId(user, storedUser);

  const authorized = roleId === 1 || roleId === 3;
  const isAdmin = roleId === 1;

  const {
    adherents,
    filteredAdherents,
    stats,

    filters,
    setFilters,

    seasons,
    dojos,
    groups,

    attendance,
    assiduityInfo,
    birthdaySet,

    loading,
    savingIds,

    refresh,
    handleToggleAttendance,
    handleDeleteAllAttendances,
    loadAssiduity,
  } = useAttendanceDashboard();

  const groupSelected = Boolean(filters.filterGroup);

  if (!authorized) {
    return <UnauthorizedPanel />;
  }

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1400px] space-y-5 px-3 sm:px-4 lg:px-6">
        <AttendanceDashboardHeader
          loading={loading}
          onRefresh={refresh}
        />

        {!groupSelected ? (
          <AttendanceGroupPickerPanel
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            seasons={seasons}
            dojos={dojos}
            groups={groups}
          />
        ) : (
          <>
            <AttendanceFiltersCard
              loading={loading}
              filters={filters}
              setFilters={setFilters}
              seasons={seasons}
              dojos={dojos}
              groups={groups}
              isAdmin={isAdmin}
              onRefresh={refresh}
              onDeleteAll={handleDeleteAllAttendances}
            />

            <AttendanceKpiCards stats={stats} loading={loading} />

            <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

              <div className="p-4 sm:p-5 lg:p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                      Appel du cours
                    </p>

                    <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                      Groupe {filters.filterGroup}
                    </h2>

                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Touchez une carte pour basculer l’état :
                      non saisi → présent → absent → non saisi.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                    <span className="text-slate-500 dark:text-slate-400">
                      Affichés :
                    </span>{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {filteredAdherents.length}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {" "}
                      / {adherents.length}
                    </span>
                  </div>
                </div>

                {loading ? (
                  <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {[...Array(10)].map((_, index) => (
                      <div
                        key={index}
                        className="h-[260px] animate-pulse rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
                      />
                    ))}
                  </div>
                ) : filteredAdherents.length === 0 ? (
                  <AttendanceEmptyState />
                ) : (
                  <div className="mt-5">
                    <AttendanceAdherentsGrid
                      adherents={filteredAdherents}
                      attendance={attendance}
                      savingIds={savingIds}
                      assiduityInfo={assiduityInfo}
                      birthdaySet={birthdaySet}
                      onToggle={handleToggleAttendance}
                      requestAssiduity={loadAssiduity}
                    />
                  </div>
                )}
              </div>
            </section>
          </>
        )}
      </div>

      <ToastContainer />
    </section>
  );
}